import type { NamedHierarchyNode } from "@contract-manager/shared";
import { useState } from "react";
import { useI18n } from "../i18n/I18n";
import { moveDestinations } from "../moveDestinations";
import { Dialog } from "./Dialog";
import type { HierarchyKind } from "./HierarchyAdmin";

/** The value the move select uses for "no parent". */
const ROOT_VALUE = "";

/** Each level of the move list is indented by one full-width space. */
const INDENT = "　";

export function RenameDialog({
  node,
  kind,
  onRename,
  onCancel,
}: {
  node: NamedHierarchyNode;
  kind: HierarchyKind;
  onRename: (name: string) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(node.name);
  const trimmed = name.trim();

  return (
    <Dialog
      title={t(`organisation.${kind}.renameTitle`)}
      submitLabel={t("organisation.tree.renameSubmit")}
      submitDisabled={trimmed === "" || trimmed === node.name}
      onSubmit={() => onRename(trimmed)}
      onCancel={onCancel}
    >
      <label className="ds-field">
        <div className="ds-text --label">
          {t(`organisation.${kind}.nameLabel`)}
        </div>
        <input
          className="ds-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={100}
          data-autofocus
        />
      </label>
    </Dialog>
  );
}

export function MoveDialog({
  node,
  nodes,
  kind,
  onMove,
  onCancel,
}: {
  node: NamedHierarchyNode;
  nodes: readonly NamedHierarchyNode[];
  kind: HierarchyKind;
  /** `null` moves the node to the top level. */
  onMove: (parentId: string | null) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const [parent, setParent] = useState(node.parentId ?? ROOT_VALUE);
  const destinations = moveDestinations(nodes, node);

  return (
    <Dialog
      title={t("organisation.tree.moveTitle", { name: node.name })}
      description={t(`organisation.${kind}.moveDescription`)}
      submitLabel={t("common.move")}
      submitDisabled={parent === (node.parentId ?? ROOT_VALUE)}
      onSubmit={() => onMove(parent === ROOT_VALUE ? null : parent)}
      onCancel={onCancel}
    >
      <label className="ds-field">
        <div className="ds-text --label">
          {t("organisation.tree.moveDestination")}
        </div>
        <select
          className="ds-select"
          value={parent}
          onChange={(event) => setParent(event.target.value)}
          data-autofocus
        >
          <option value={ROOT_VALUE}>{t("organisation.tree.topLevel")}</option>
          {destinations.map((destination) => (
            <option key={destination.id} value={destination.id}>
              {INDENT.repeat(destination.depth + 1)}
              {destination.name}
            </option>
          ))}
        </select>
      </label>
    </Dialog>
  );
}
